import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  CircularProgress, 
  Alert,
  Paper,
  Divider,
  Avatar
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { format } from 'date-fns';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Components
import StatusChip from '../components/ui/StatusChip';
import { useAuth } from '../contexts/AuthContext';
import '../styles/shared.css';
import './Vote.css';

const Vote = () => {
  const { user } = useAuth();
  const [election, setElection] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedCandidates, setSelectedCandidates] = useState([]); // Array of selected candidate IDs
  const [userVotes, setUserVotes] = useState([]); // Array of user's existing votes
  const [noOpenElection, setNoOpenElection] = useState(false); // Track when there are no open elections
  const [nextElection, setNextElection] = useState(null); // Track the next upcoming election

  // Function to fetch user votes
  const fetchUserVotes = useCallback(async (electionId) => {
    if (!electionId) return;

    try {
      const userVotesRes = await axios.get(`/api/votes/user_all/${electionId}`);
      setUserVotes(userVotesRes.data || []);
      setSelectedCandidates(userVotesRes.data?.map(v => v.nomination_id) || []);
    } catch (err) {
      console.error('Failed to fetch user votes:', err);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch all elections
        const electionsRes = await axios.get('/api/elections/');

        // Find the open election
        const now = new Date();
        let openElection = null;

        // Find the election that's currently open
        for (const election of electionsRes.data) {
          const votingStartDate = new Date(election.voting_start_date);
          const votingEndDate = new Date(election.voting_end_date);
          const isElectionOpen = now >= votingStartDate && now <= votingEndDate;

          if (isElectionOpen) {
            openElection = election;
            console.log('Found open election:', election);
            break;
          }
        }

        if (!openElection) {
          console.log('No open election found');

          // Look for the next upcoming election
          let upcomingElection = null;
          for (const election of electionsRes.data) {
            const votingStartDate = new Date(election.voting_start_date);
            if (votingStartDate > now) {
              if (!upcomingElection || votingStartDate < new Date(upcomingElection.voting_start_date)) {
                upcomingElection = election;
              }
            }
          }

          setNextElection(upcomingElection);
          setError('No open election found');
          setLoading(false);
          setNoOpenElection(true);
          return;
        }

        // Get all approved nominations for the election
        const nominationsRes = await axios.get(`/api/nominations/?election_id=${openElection.id}&status=approved`);
        const allNominations = nominationsRes.data;

        console.log('All nominations:', allNominations);

        setElection(openElection);
        setCandidates(allNominations);

        // Fetch user votes
        await fetchUserVotes(openElection.id);

        setLoading(false);
      } catch (err) {
        console.error('Error in fetchData:', err);
        setError(err.message || 'An error occurred');
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const handleCandidateToggle = (candidateId) => {
    const seatsToContest = election?.seats_to_contest || [1, 2, 3, 4, 5];
    const voteLimit = seatsToContest.length;

    if (selectedCandidates.includes(candidateId)) {
      // Remove candidate from selection
      setSelectedCandidates(prev => prev.filter(id => id !== candidateId));
    } else {
      // Add candidate to selection if under limit
      if (selectedCandidates.length < voteLimit) {
        setSelectedCandidates(prev => [...prev, candidateId]);
      } else {
        setError(`You can only vote for ${voteLimit} candidate${voteLimit > 1 ? 's' : ''}`);
      }
    }
  };

  const handleVoteSubmit = async () => {
    if (selectedCandidates.length === 0) {
      setError('Please select at least one candidate before voting.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const token = localStorage.getItem('accessToken');

      console.log('Current userVotes:', userVotes);
      console.log('Current selectedCandidates:', selectedCandidates);

      // Find votes to add (selected but not yet voted)
      const votesToAdd = selectedCandidates.filter(
        nominationId => !userVotes.some(v => v.nomination_id === nominationId)
      );

      // Find votes to remove (voted but no longer selected)
      const votesToRemove = userVotes.filter(
        vote => !selectedCandidates.includes(vote.nomination_id)
      );

      console.log('Votes to add:', votesToAdd);
      console.log('Votes to remove:', votesToRemove);

      // Remove votes that are no longer selected
      for (const vote of votesToRemove) {
        console.log('Deleting vote:', vote.id);
        await axios.delete(`/api/votes/${vote.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        });
      }

      // Add new votes
      for (const nominationId of votesToAdd) {
        const votePayload = {
          election_id: Number(election.id),
          nomination_id: Number(nominationId)
        };

        console.log('Adding vote for nomination:', nominationId);
        await axios.post('/api/votes/', votePayload, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        });
      }

      setSuccess('Your votes have been updated successfully!');
      await fetchUserVotes(election.id);

    } catch (err) {
      console.error('Vote submission error:', err);
      if (err.response?.status === 401) {
        setError('Your session has expired. Please refresh the page and try again.');
      } else {
        setError(err.response?.data?.detail || 'Failed to update your votes. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const checkVotingEligibility = () => {
    if (!election) return false;

    const now = new Date();
    const votingStart = new Date(election.voting_start_date);
    const votingEnd = new Date(election.voting_end_date);

    const isWithinVotingPeriod = now >= votingStart && now <= votingEnd;

    return isWithinVotingPeriod;
  };
  
  const getVotingStatus = () => {
    if (!election) {
      return {
        status: "unavailable",
        message: "No election data available. Please refresh the page."
      };
    }

    if (!candidates || candidates.length === 0) {
      return {
        status: "no-candidates",
        message: "No candidates are running in this election."
      };
    }

    const now = new Date();
    const votingStart = new Date(election.voting_start_date);
    const votingEnd = new Date(election.voting_end_date);

    if (now < votingStart) {
      return {
        status: "upcoming",
        message: `Voting opens on ${format(votingStart, 'MMM d, yyyy')}`
      };
    } else if (now > votingEnd) {
      return {
        status: "closed",
        message: "The voting period has ended."
      };
    } else {
      return {
        status: "open",
        message: "Voting is open! You can change your votes at any time."
      };
    }
  };

  if (loading) {
    return (
      <div className="vote-container">
        <div className="vote-header">
          <Typography className="vote-title">
            Cast Your Vote
          </Typography>
        </div>

        <div className="loading-container">
          <CircularProgress className="loading-spinner" size={60} />
        </div>
      </div>
    );
  }

  const votingStatus = getVotingStatus();

  
  // Early return for no open elections - show simple message instead of complex UI
  if (noOpenElection) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" sx={{ mb: 4, fontWeight: 600, color: '#2d3748' }}>
          Cast Your Vote
        </Typography>

        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: '14px',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            textAlign: 'center'
          }}
        >
          <Typography variant="h5" component="h2" sx={{ mb: 3, color: '#2d3748' }}>
            No Open Works Council Election
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Typography sx={{ fontSize: 60, opacity: 0.6 }}>🏛️</Typography>
          </Box>
          
          <Typography variant="body1" sx={{ textAlign: 'center', mb: 2, color: '#4a5568' }}>
            There are currently no open works council elections.
          </Typography>
          
          {nextElection ? (
            <Box sx={{ mt: 3, p: 2, backgroundColor: 'rgba(0, 153, 153, 0.05)', borderRadius: '10px', border: '1px solid rgba(0, 153, 153, 0.2)' }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#009999', fontWeight: 600 }}>
                📅 Next Voting Period
              </Typography>
              <Typography variant="body1" sx={{ mb: 1, color: '#2d3748', fontWeight: 500 }}>
                {nextElection.name}
              </Typography>
              <Typography variant="body2" sx={{ color: '#4a5568' }}>
                Voting opens: <strong>{format(new Date(nextElection.voting_start_date), 'MMM d, yyyy')}</strong>
              </Typography>
              <Typography variant="body2" sx={{ color: '#4a5568' }}>
                Voting closes: <strong>{format(new Date(nextElection.voting_end_date), 'MMM d, yyyy')}</strong>
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ textAlign: 'center', color: '#6b7280', mt: 2 }}>
              Please check back later or contact an administrator for more information.
            </Typography>
          )}
        </Paper>
      </Box>
    );
  }

  return (
    <div className="vote-container">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography className="vote-title" sx={{ mb: 0.5 }}>
              Works Council Election
            </Typography>
            {election && (
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                {format(new Date(election.voting_start_date), 'MMM d')} - {format(new Date(election.voting_end_date), 'MMM d, yyyy')}
              </Typography>
            )}
          </Box>
          <Typography variant="body1" sx={{ fontWeight: 600, color: '#009999' }}>
            {userVotes.length} / {(election?.seats_to_contest || [1, 2, 3, 4, 5]).length} votes cast
          </Typography>
        </Box>
      </motion.div>

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <Alert 
              severity="error" 
              sx={{ mb: 2, mt: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          </motion.div>
        )}
        {success && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <Alert 
              severity="success" 
              sx={{ mb: 2, mt: 2 }}
              onClose={() => setSuccess(null)}
            >
              {success}
            </Alert>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      >
        {candidates.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 3 }}>
              {candidates.map((candidate, index) => {
                const isSelected = selectedCandidates.includes(candidate.id);
                const isVoted = userVotes.some(v => v.nomination_id === candidate.id);
                
                return (
                  <Paper
                    key={candidate.id}
                    sx={{
                      p: 3,
                      borderRadius: '12px',
                      border: isSelected ? '2px solid #009999' : '1px solid rgba(0,0,0,0.12)',
                      backgroundColor: isSelected ? 'rgba(0, 153, 153, 0.05)' : 'white',
                      cursor: votingStatus.status === 'open' ? 'pointer' : 'default',
                      transition: 'all 0.2s ease',
                      '&:hover': votingStatus.status === 'open' ? {
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        transform: 'translateY(-2px)'
                      } : {}
                    }}
                    onClick={() => votingStatus.status === 'open' && handleCandidateToggle(candidate.id)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                      <Avatar 
                        src={candidate.candidate?.profile_picture}
                        sx={{ width: 56, height: 56 }}
                      >
                        {candidate.candidate?.name?.charAt(0) || '?'}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {candidate.candidate?.name || 'Candidate'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {candidate.candidate?.business_unit || ''}
                        </Typography>
                      </Box>
                      {isVoted && (
                        <CheckCircleIcon sx={{ color: '#10b981', fontSize: 24 }} />
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, lineHeight: 1.5 }}>
                      {candidate.bio || candidate.statement || 'No statement provided'}
                    </Typography>
                    {isSelected && (
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1, 
                        color: '#009999',
                        fontWeight: 600,
                        fontSize: '0.9rem'
                      }}>
                        <CheckCircleIcon sx={{ fontSize: 16 }} />
                        Selected
                      </Box>
                    )}
                  </Paper>
                );
              })}
            </Box>
            
            {votingStatus.status === 'open' && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  disabled={selectedCandidates.length === 0 || submitting}
                  onClick={handleVoteSubmit}
                  sx={{
                    py: 1.5,
                    px: 6,
                    borderRadius: '12px',
                    fontSize: '1rem',
                    fontWeight: 600,
                    boxShadow: '0 4px 20px rgba(0, 153, 153, 0.3)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 6px 25px rgba(0, 153, 153, 0.4)'
                    }
                  }}
                >
                  {submitting ? (
                    <CircularProgress size={24} sx={{ color: 'white' }} />
                  ) : (
                    `Cast ${selectedCandidates.length} Vote${selectedCandidates.length !== 1 ? 's' : ''}`
                  )}
                </Button>
              </Box>
            )}
          </Box>
        )}
        
        {candidates.length === 0 && (
          <Box sx={{ textAlign: 'center', mt: 4, p: 3, backgroundColor: 'rgba(0,0,0,0.02)', borderRadius: '12px' }}>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              No candidates available for this election
            </Typography>
          </Box>
        )}
      </motion.div>
    </div>
  );
};


export default Vote;
